import { DavetYenidenGonder } from "./DavetYenidenGonder";
import { useRef, useState } from "react";
import { TcKimlikDuzenle } from "./TcKimlikDuzenle";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ONBOARDING_PENCERE_METNI } from "@koclukpanel/contracts";
import { Alan, BosDurum, Buton, Iskelet, Kart, Rozet, Secim, Tablo } from "@koclukpanel/ui";
import {
  api,
  ApiHatasi,
  atamaListesi,
  atanabilirUzmanlar,
  devirGecmisi,
  dosyaBaglantisi,
  dosyaKotasi,
  dosyaListesi,
  dosyaSil,
  dosyaYukle,
  ogrenciListesi,
  ogrenciProfilGuncelle,
  ogrenciTcKimlikDegistir,
  onboardingDurumu,
  uzmanAta,
  uzmanDevret,
  veliBagiKaldir,
  veliBagla,
  veliIsaretGuncelle,
  veliListesi,
  type DosyaTuru,
  type OgrenciSatiri,
  type VeliOzeti,
} from "./api";
import { SINIFLAR, type Sinif } from "./ogrenci-kayit";
import { SayfaBasligiEki } from "./Kabuk";

/**
 * ÖĞRENCİ DOSYASI — veli · uzman ataması · dosyalar
 *
 * ================================================================
 * ÜÇÜ BİR ARADA, ÇÜNKÜ ÜÇÜ DE "BU ÖĞRENCİ KİMİN"
 * ================================================================
 * Veli bağlama (F-PPL-03), uzman ataması ve devri (F-PPL-10) ve
 * öğrenci dosyaları (F-SYS-03) — üçünün de ucu vardı, hiçbirinin
 * ekranı yoktu. Ayrı ayrı menü girdisi açmak, "öğrenciyi seç"
 * adımını üç kez tekrarlatırdı.
 */

const DOSYA_TURU: Record<DosyaTuru, string> = {
  PDF: "PDF belge",
  IMAGE: "Görsel",
  REPORT: "Rapor",
  RECEIPT: "Dekont / makbuz",
  AVATAR: "Profil görseli",
  OTHER: "Diğer",
};

function boyut(bayt: number): string {
  if (bayt < 1024) return `${bayt} B`;
  if (bayt < 1024 * 1024) return `${(bayt / 1024).toFixed(1)} KB`;
  return `${(bayt / 1024 / 1024).toFixed(1)} MB`;
}

function gun(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR");
}

export function OgrenciDosyasi({
  ogrenciId,
  ogrenciAdi,
  veliBaglayabilir,
  veliKaldirabilir,
  duzenleyebilir = false,
}: {
  ogrenciId: string;
  ogrenciAdi: string;
  /** `kisi.veli.bagla` — matris §5.1 'C'. Yonetim ve satista var. */
  veliBaglayabilir: boolean;
  /** `kisi.veli.kaldir` — matris §5.1 'D'. YALNIZCA yonetimde. */
  veliKaldirabilir: boolean;
  /** `kisi.ogrenci.duzenle` — profil formu buna bagli. */
  duzenleyebilir?: boolean;
}) {
  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki aciklama={`${ogrenciAdi} — profil, veli, uzman ataması ve dosyalar.`} />
      <Profil ogrenciId={ogrenciId} duzenleyebilir={duzenleyebilir} />
      <Onboarding ogrenciId={ogrenciId} />
      <UzmanAtamasi ogrenciId={ogrenciId} atayabilir={duzenleyebilir} />
      <Veliler
        ogrenciId={ogrenciId}
        ogrenciAdi={ogrenciAdi}
        baglayabilir={veliBaglayabilir}
        kaldirabilir={veliKaldirabilir}
      />
      <Dosyalar ogrenciId={ogrenciId} />
    </div>
  );
}

// =====================================================================
// UZMAN ATAMASI VE DEVRİ — F-PPL-10
// =====================================================================

function UzmanAtamasi({
  ogrenciId,
  atayabilir,
}: {
  ogrenciId: string;
  /**
   * `kisi.ogrenci.duzenle` — ATANABILIR UZMAN LISTESININ izni.
   *
   * ================================================================
   * NEDEN AYRI BIR BAYRAK — 12.09.2026'da canlida olculdu
   * ================================================================
   * `atanabilirUzmanlar` sorgusu KOSULSUZ atiliyordu. Ucun izni
   * `kisi.ogrenci.duzenle`; Akademik Koordinator ve Finans o izni
   * TASIMIYOR ve ekran her acilista 403 aliyordu.
   *
   * Bayrak zaten vardi ve `Profil`e geciriliyordu (`duzenleyebilir`);
   * yalnizca buraya kadar gelmemisti. Yeni bir izin sorusu
   * eklenmedi — VAR OLAN cevap eksik yere tasindi.
   *
   * ⚠️ Bu bir GUVENLIK kontrolu DEGIL, gurultu onleme. Yetkiyi
   * sunucu uyguluyor; burasi yalnizca yapilamayacak istegi atmiyor.
   */
  atayabilir: boolean;
}) {
  const qc = useQueryClient();
  const [devirAcik, setDevirAcik] = useState(false);
  const [hedef, setHedef] = useState("");
  const [gerekce, setGerekce] = useState("");
  const [devirNotu, setDevirNotu] = useState("");
  const [notlarDevret, setNotlarDevret] = useState(false);
  const [bilgi, setBilgi] = useState<string | null>(null);

  const atamalar = useQuery({
    queryKey: ["atamalar", ogrenciId],
    queryFn: () => atamaListesi(ogrenciId),
    retry: false,
  });
  const uzmanlar = useQuery({
    queryKey: ["atanabilir-uzmanlar", ogrenciId],
    queryFn: () => atanabilirUzmanlar(ogrenciId),
    enabled: atayabilir,
    retry: false,
  });
  const gecmis = useQuery({
    queryKey: ["devir-gecmisi", ogrenciId],
    queryFn: () => devirGecmisi(ogrenciId),
    retry: false,
  });

  const tazele = async (): Promise<void> => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["atamalar", ogrenciId] }),
      qc.invalidateQueries({ queryKey: ["devir-gecmisi", ogrenciId] }),
    ]);
  };

  const ata = useMutation({
    mutationFn: () => uzmanAta(ogrenciId, hedef),
    onSuccess: async () => {
      setBilgi("Uzman atandı.");
      setHedef("");
      await tazele();
    },
  });

  const devret = useMutation({
    mutationFn: () =>
      uzmanDevret(ogrenciId, {
        toCoachId: hedef,
        reason: gerekce.trim(),
        handoverNote: devirNotu.trim() || null,
        transferProtectedNotes: notlarDevret,
      }),
    onSuccess: async () => {
      setBilgi("Devir yapıldı. Eski uzmanın erişimi kesildi.");
      setDevirAcik(false);
      setHedef("");
      setGerekce("");
      setDevirNotu("");
      setNotlarDevret(false);
      await tazele();
    },
  });

  if (atamalar.isPending) return <Iskelet satir={2} />;

  if (atamalar.isError) {
    return (
      <Kart baslik="Uzman ataması">
        <BosDurum
          ton="hata"
          baslik="Atamalar alınamadı"
          aciklama={atamalar.error instanceof ApiHatasi ? atamalar.error.message : undefined}
        />
      </Kart>
    );
  }

  const gecerli = atamalar.data.filter((a) => a.gecerli);
  const gecmisAtama = atamalar.data.filter((a) => !a.gecerli);
  /*
    ================================================================
    IZIN 403'TEN CIKARILMIYOR, SORULUYOR — 12.09.2026
    ================================================================
    Burasi `!uzmanlar.isError` idi: ekran izni, ISTEGIN DUSMESINDEN
    ogreniyordu. Calisiyordu ama bedeli, izni olmayan her kullanicida
    her acilista bir 403 uretmekti — Akademik Koordinator ve Finans
    hesaplarinda canli taramada goruldu.

    Sorguyu kapatip bu satiri OLDUGU GIBI birakmak DAHA KOTU olurdu:
    istek hic atilmayinca `isError` hicbir zaman `true` olmaz ve
    `duzenleyebilir` TRUE'ya doner — atama formu tam da yetkisi
    olmayan kisiye acilirdi. Gurultuyu susturup yerine yanlis bir
    vaat koymak olurdu.

    Bu yuzden ikisi birlikte degisti: sorgu `atayabilir` ile
    kosullandi, karar da ayni bayraga baglandi. Tek kaynak.
  */
  const duzenleyebilir = atayabilir;

  return (
    <Kart
      baslik="Uzman ataması"
      aciklama="Öğrenciden kim sorumlu. Değişiklik ATAMA ile değil DEVİR ile yapılır — devir gerekçe, devir notu ve erişim bitiş tarihi kaydı üretir."
    >
      <div className="flex flex-col gap-3">
        {gecerli.length === 0 ? (
          <p className="text-uyari-600 text-sm font-medium">
            Bu öğrencinin atanmış uzmanı YOK. Uzman atanmadan program hazırlanamaz ve öğrenci hiçbir
            iş listesine düşmez.
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {gecerli.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-notr-900 font-medium">
                  {a.firstName} {a.lastName}
                </span>
                {a.isPrimary && <Rozet ton="onay">Birincil</Rozet>}
                <span className="text-notr-600">{gun(a.assignedAt)}’den beri</span>
                {a.endedAt && (
                  /*
                    BİTMİŞ ATAMA AYRI GÖSTERİLİYOR. Liste geçmiş atamaları
                    da içeriyor; hangisinin şu an sorumlu olduğu tek
                    bakışta anlaşılmalı.

                    Eskiden burada "erişimi {tarih} tarihinde kesilecek"
                    yazıyordu — devir penceresi vardı. 08.08.2026'da
                    kaldırıldı (F-PPL-10): erişim devir anında kesiliyor,
                    yani `endedAt` dolu olan atamanın erişimi ZATEN yok.
                  */
                  <Rozet ton="notr">Erişimi {gun(a.endedAt)} tarihinde kesildi</Rozet>
                )}
              </li>
            ))}
          </ul>
        )}

        {bilgi && (
          <p role="status" className="text-notr-700 text-sm">
            {bilgi}
          </p>
        )}
        {(ata.isError || devret.isError) && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {(ata.error ?? devret.error) instanceof ApiHatasi
              ? ((ata.error ?? devret.error) as ApiHatasi).message
              : "İşlem tamamlanamadı."}
          </p>
        )}

        {!duzenleyebilir ? (
          <p className="text-notr-600 text-sm">
            Atama ve devir için yetkiniz yok. Yetkiniz olmaması, bu öğrencinin uzmanının
            değiştirilemeyeceği anlamına gelmez.
          </p>
        ) : uzmanlar.isError ? (
          /*
            LISTE DUSTUGUNDE SESSIZ KALINMIYOR.

            Izin `atayabilir` ile ayrildiktan sonra geriye kalan hata
            GERCEK bir hatadir (ag, sunucu). Once bu dal yoktu cunku
            her hata "izin yok" sayiliyordu; ayrilinca TypeScript
            `uzmanlar.data`nin tanimsiz olabilecegini gosterdi ve
            eksik olan da tam olarak buydu. `?? []` yazmak derlemeyi
            susturur, kullaniciya BOS BIR SECIM KUTUSU gosterirdi:
            "atanabilecek uzman yok" ile "liste gelmedi" ayni sey
            degil.
          */
          <p className="text-hata-700 text-sm">
            Atanabilecek uzmanların listesi alınamadı. Sayfayı yenileyin; sürerse teknik ekibe
            bildirin.
          </p>
        ) : uzmanlar.isPending ? (
          <Iskelet satir={1} />
        ) : gecerli.length === 0 ? (
          <div className="flex flex-wrap items-end gap-2">
            <Secim
              etiket="Uzman"
              value={hedef}
              onChange={(e) => setHedef(e.target.value)}
              className="w-72"
            >
              <option value="">Seçin…</option>
              {/*
                SEÇENEK METNİ SEVİYEYİ VE GEREKÇESİNİ TAŞIYOR — F-PPL-12.
                Eskiden yalnızca "Ad — 12 öğrenci" yazıyordu ve bu,
                maddenin ilk cümlesine aykırıydı: öğrenci sayısı tek
                başına iş yükü değil. Kotasında yeri olan ama on iki
                bekleyen işi olan uzman, sayıya bakıldığında en uygun
                aday görünüyordu.

                `<option>` biçimlendirme kabul etmiyor; taşınabilecek
                tek şey metin. Ayrıntılı tablo seçim yapıldıktan sonra
                aşağıda çiziliyor.
              */}
              {uzmanlar.data.map((u) => (
                <option
                  key={u.coachId}
                  value={u.coachId}
                  /*
                    İZİNLİ UZMAN SEÇİLEMİYOR — F-PPL-11b: "o uzmana yeni
                    öğrenci ATANAMAZ". Sunucu zaten reddediyor
                    (`UZMAN_IZINDE`); burada kapatmanın sebebi kullanıcıyı
                    seçtirip sonra reddetmemek.

                    LİSTEDEN ÇIKARILMIYOR: çıkarsaydık kullanıcı aradığı
                    kişiyi bulamaz ve neden bulamadığını da öğrenemezdi.
                    "İzinde — yeni öğrenci atanamaz" yazan soluk bir satır,
                    olmayan bir satırdan çok daha fazlasını söylüyor.
                  */
                  disabled={u.yuk.seviye === "izinli"}
                >
                  {u.ad} — {u.yuk.gerekce}
                </option>
              ))}
            </Secim>
            <Buton
              yukleniyor={ata.isPending}
              disabled={!hedef}
              onClick={() => {
                setBilgi(null);
                ata.mutate();
              }}
            >
              Ata
            </Buton>
          </div>
        ) : !devirAcik ? (
          <div>
            <Buton gorunum="ikincil" onClick={() => setDevirAcik(true)}>
              Başka uzmana devret
            </Buton>
          </div>
        ) : (
          <div className="rounded-kart border-notr-200 shadow-kart bg-notr-50 flex flex-col gap-3 border p-3">
            <Secim etiket="Yeni uzman" value={hedef} onChange={(e) => setHedef(e.target.value)}>
              <option value="">Seçin…</option>
              {uzmanlar.data
                .filter((u) => !gecerli.some((a) => a.coachId === u.coachId))
                .map((u) => (
                  <option
                    key={u.coachId}
                    value={u.coachId}
                    // İzinli uzmana devir de yapılamaz — aynı kural.
                    disabled={u.yuk.seviye === "izinli"}
                  >
                    {u.ad} — {u.yuk.gerekce}
                  </option>
                ))}
            </Secim>

            <Alan
              etiket="Devir gerekçesi (zorunlu)"
              value={gerekce}
              onChange={(e) => setGerekce(e.target.value)}
              yardim="Altı ay sonra “bu öğrenci neden el değiştirdi” sorusunu cevaplayacak cümle."
            />

            <Alan
              etiket="Devir notu"
              value={devirNotu}
              onChange={(e) => setDevirNotu(e.target.value)}
              yardim="Yeni uzmanın bilmesi gerekenler. İsteğe bağlı."
            />

            {/*
              ERİŞİM BİTİŞ TARİHİ ALANI KALDIRILDI — 08.08.2026, F-PPL-10.
              Burada bir tarih seçiliyor ve eski uzman o tarihe kadar
              öğrencinin dosyasını görmeye devam ediyordu. Karar değişti:
              erişim devir anında kesilir. Alanı bırakıp "artık işlemiyor"
              demek en kötüsü olurdu — kullanıcıya kontrol ettiğini
              sandıran ama hiçbir şey yapmayan bir alan.
            */}
            <p className="rounded-kart shadow-kart border-uyari-200 bg-uyari-50 text-uyari-800 border px-3 py-2 text-sm">
              Devir onaylandığında eski uzmanın bu öğrenciye erişimi <strong>anında</strong>{" "}
              kesilir. Aktarmak istediğiniz bilgiyi devir notuna yazın.
            </p>

            {/*
              KORUMALI NOTLAR VARSAYILAN OLARAK DEVREDİLMİYOR.
              Öğrencinin psikolojik danışmanlık notu, uzman değişti diye
              otomatik el değiştirmemeli; devretmek AYRI ve BİLİNÇLİ bir
              karar olmalı.
            */}
            <label className="min-h-dokunma flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="accent-marka-600 mt-0.5 size-5"
                checked={notlarDevret}
                onChange={(e) => setNotlarDevret(e.target.checked)}
                aria-describedby="korumali-devir-aciklama"
              />
              <span className="flex flex-col gap-0.5">
                <span className="text-notr-800 text-sm">
                  Korumalı PDR notları da yeni uzmana devredilsin
                </span>
                {/* K15 (Emre 06.10.2026) — ne geçtiği ve ne geçmediği açıkça yazılı. */}
                <span id="korumali-devir-aciklama" className="text-notr-600 text-xs">
                  İşaretlemezseniz yeni uzman, önceki uzmanın korumalı notlarını göremez.
                  İşaretlerseniz yalnız bu öğrencinin, devirden önce yazılmış notları geçer; her
                  okuma yine gerekçe ister ve kayda geçer.
                </span>
              </span>
            </label>

            <div className="flex gap-2">
              <Buton
                yukleniyor={devret.isPending}
                disabled={!hedef || gerekce.trim().length < 3}
                onClick={() => {
                  setBilgi(null);
                  devret.mutate();
                }}
              >
                Devri tamamla
              </Buton>
              <Buton gorunum="sessiz" onClick={() => setDevirAcik(false)}>
                Vazgeç
              </Buton>
            </div>
          </div>
        )}

        {gecmisAtama.length > 0 && (
          <details className="text-sm">
            <summary className="min-h-dokunma text-notr-700 cursor-pointer py-2">
              Geçmiş atamalar ({gecmisAtama.length})
            </summary>
            <ul className="flex flex-col gap-1 pt-1">
              {gecmisAtama.map((a) => (
                <li key={a.id} className="text-notr-600">
                  {a.firstName} {a.lastName} · {gun(a.assignedAt)} –{" "}
                  {a.endedAt ? gun(a.endedAt) : "?"}
                </li>
              ))}
            </ul>
          </details>
        )}

        {gecmis.data && gecmis.data.length > 0 && (
          <details className="text-sm">
            <summary className="min-h-dokunma text-notr-700 cursor-pointer py-2">
              Devir geçmişi ({gecmis.data.length})
            </summary>
            <ul className="flex flex-col gap-2 pt-1">
              {gecmis.data.map((d) => (
                <li key={d.id} className="text-notr-600">
                  <span className="text-notr-800">
                    {d.fromCoachAd ?? "—"} → {d.toCoachAd}
                  </span>{" "}
                  · {gun(d.transferredAt)}
                  <br />
                  <span className="text-notr-600">{d.reason}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </Kart>
  );
}

// =====================================================================
// VELİLER — F-PPL-03
// =====================================================================

const ISARETLER = [
  ["isPrimary", "Birincil iletişim"],
  ["receivesReport", "Veli raporu alır"],
  ["receivesBilling", "Fatura/ödeme yazışması alır"],
  ["hasPortalAccess", "Veli portalına erişir"],
  ["isLegalGuardian", "Yasal veli"],
] as const;

/**
 * Veli bolumu.
 *
 * ================================================================
 * UC EYLEM, UC AYRI IZIN — ve ikisi burada GIZLENEBILIR
 * ================================================================
 * Isaret kutulari (duzenleme) bu bolumu goren herkeste acik: `Veliler`
 * kartini gormek zaten `kisi.veli.goruntule` istiyor ve matriste okuma
 * hakki olan her rolde duzenleme de var (§5.1 — R'siz U yok).
 *
 * "Veli ekle" ve "Baglantiyi kaldir" ise ayri izinlere bagli. Dugmeyi
 * herkese gosterip sunucunun reddetmesini beklemek CIKMAZ SOKAK
 * uretiyordu: uzman tikliyor, 403 aliyor, sebebini bilmiyor. Yetkisi
 * olmayan oge KILITLI degil GIZLI — kullaniciya erisemeyecegi bir seyin
 * varligini duyurmuyoruz.
 *
 * Sunucu yine de tek gercek kapi: bu bayraklar gorunumu belirliyor,
 * yetkiyi degil.
 */
function Veliler({
  ogrenciId,
  ogrenciAdi,
  baglayabilir,
  kaldirabilir,
}: {
  ogrenciId: string;
  ogrenciAdi: string;
  baglayabilir: boolean;
  kaldirabilir: boolean;
}) {
  const qc = useQueryClient();
  const [formAcik, setFormAcik] = useState(false);
  const [ad, setAd] = useState("");
  const [soyad, setSoyad] = useState("");
  const [eposta, setEposta] = useState("");
  const [telefon, setTelefon] = useState("");
  /** G-AUT-00 — velinin T.C. kimlik numarası. İsteğe bağlı. */
  const [tcKimlik, setTcKimlik] = useState("");
  /**
   * C39 — ortak numarada kimin telefonuyla gireceği.
   *
   * Bu form C39'un ASIL vakası: veli numarası çoğu zaman öğrencinin
   * kaydında da duruyor ve o numarayla kimin gireceği belirsiz kalıyor.
   */
  const [telefonSahibi, setTelefonSahibi] = useState(false);
  const [yakinlik, setYakinlik] = useState("Anne");
  const [kaldirilan, setKaldirilan] = useState<string | null>(null);
  const [kaldirGerekce, setKaldirGerekce] = useState("");
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [basarisizDavetBagId, setBasarisizDavetBagId] = useState<string | null>(null);

  const liste = useQuery({
    queryKey: ["veliler", ogrenciId],
    queryFn: () => veliListesi(ogrenciId),
    retry: false,
  });

  const tazele = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ["veliler", ogrenciId] });
  };

  const bagla = useMutation({
    mutationFn: () =>
      veliBagla(ogrenciId, {
        email: eposta.trim(),
        firstName: ad.trim(),
        lastName: soyad.trim(),
        phone: telefon.trim() || null,
        // Boş alan `null` gidiyor, `""` değil: `""` benzersizlik
        // kısıtına takılır ve T.C.'si girilmemiş ikinci veli
        // eklenemezdi.
        nationalId: tcKimlik.trim() || null,
        phoneIsPrimary: telefonSahibi,
        relation: yakinlik,
        davetGonder: true,
        isPrimary: (liste.data?.length ?? 0) === 0,
        receivesReport: true,
        hasPortalAccess: true,
        isLegalGuardian: true,
      }),
    onSuccess: async (s) => {
      setBasarisizDavetBagId(s.davetGonderildi === false ? s.id : null);
      setBilgi(
        s.yeniHesap
          ? s.davetGonderildi === false
            ? "Veli bağlandı ve hesabı açıldı. Davet postası gönderilemedi — yeniden gönderin"
            : "Veli bağlandı ve hesabı açılıp davet gönderildi."
          : "Veli bağlandı. Bu e-posta zaten sistemde kayıtlıydı; yeni hesap AÇILMADI, mevcut hesap kullanıldı.",
      );
      setFormAcik(false);
      setAd("");
      setSoyad("");
      setEposta("");
      setTelefon("");
      await tazele();
    },
  });

  const isaret = useMutation({
    mutationFn: (g: { bagId: string; alan: string; deger: boolean }) =>
      veliIsaretGuncelle(ogrenciId, g.bagId, { [g.alan]: g.deger }),
    onSuccess: tazele,
  });

  const dogrulamaGonder = useMutation({
    mutationFn: (bagId: string) =>
      api(
        `/ogrenciler/${encodeURIComponent(ogrenciId)}/veliler/${encodeURIComponent(bagId)}/dogrulama/gonder`,
        { yontem: "POST" },
      ),
    onSuccess: () => setBilgi("Doğrulama bağlantısı velinin kendi e-posta adresine gönderildi."),
    retry: false,
  });

  const kaldir = useMutation({
    mutationFn: (bagId: string) => veliBagiKaldir(ogrenciId, bagId, kaldirGerekce.trim()),
    onSuccess: async () => {
      setBilgi(
        "Veli bağlantısı kaldırıldı. Veli HESABI silinmedi — başka bir öğrencinin velisi olabilir.",
      );
      setKaldirilan(null);
      setKaldirGerekce("");
      await tazele();
    },
  });

  if (liste.isPending) return <Iskelet satir={2} />;

  if (liste.isError) {
    return (
      <Kart baslik="Veliler">
        <BosDurum
          ton="hata"
          baslik="Veliler alınamadı"
          aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
        />
      </Kart>
    );
  }

  return (
    <Kart
      baslik="Veliler"
      aciklama="Veli portalına erişim, rapor ve fatura yazışması bu işaretlere bağlı."
      eylem={
        baglayabilir ? (
          <Buton gorunum="ikincil" onClick={() => setFormAcik((a) => !a)} aria-expanded={formAcik}>
            {formAcik ? "Formu kapat" : "Veli ekle"}
          </Buton>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3">
        {bilgi && (
          <p role="status" className="rounded-kart bg-notr-50 text-notr-800 px-3 py-2 text-sm">
            {bilgi}
          </p>
        )}
        {basarisizDavetBagId && (
          <DavetYenidenGonder
            hedef={{ veliBagId: basarisizDavetBagId }}
            izinli={baglayabilir}
            onGonderildi={() => {
              setBilgi("Davet gönderildi.");
              setBasarisizDavetBagId(null);
            }}
          />
        )}
        {(bagla.isError || isaret.isError || kaldir.isError || dogrulamaGonder.isError) && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {(bagla.error ?? isaret.error ?? kaldir.error ?? dogrulamaGonder.error) instanceof
            ApiHatasi
              ? (
                  (bagla.error ??
                    isaret.error ??
                    kaldir.error ??
                    dogrulamaGonder.error) as ApiHatasi
                ).message
              : "İşlem tamamlanamadı."}
          </p>
        )}

        {formAcik && (
          <div className="rounded-kart border-notr-200 shadow-kart bg-notr-50 flex flex-col gap-3 border p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Alan etiket="Ad" value={ad} onChange={(e) => setAd(e.target.value)} />
              <Alan etiket="Soyad" value={soyad} onChange={(e) => setSoyad(e.target.value)} />
              <Alan
                etiket="E-posta"
                type="email"
                value={eposta}
                onChange={(e) => setEposta(e.target.value)}
                yardim="Davet buraya gider. Bu adres sistemde varsa YENİ hesap açılmaz."
              />
              <Alan etiket="Telefon" value={telefon} onChange={(e) => setTelefon(e.target.value)} />
              <Alan
                etiket="T.C. kimlik numarası"
                value={tcKimlik}
                onChange={(e) => setTcKimlik(e.target.value)}
                inputMode="numeric"
                yardim="İsteğe bağlı. Girilirse veli bu numarayla da giriş yapabilir."
              />
            </div>
            {/*
              Kutu yalnızca telefon yazılınca görünüyor — telefonu
              olmayan birine "numaranın sahibi mi" diye sormak anlamsız.
            */}
            {telefon.trim().length > 0 && (
              <label className="text-notr-700 flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={telefonSahibi}
                  onChange={(e) => setTelefonSahibi(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Bu numaranın sahibi bu veli
                  <span className="text-notr-600 block text-xs">
                    Aynı numara öğrencinin kaydında da varsa telefonla girişi sahibi yapar; öteki
                    kişi e-postasıyla girer.
                  </span>
                </span>
              </label>
            )}
            <Secim
              etiket="Yakınlık"
              value={yakinlik}
              onChange={(e) => setYakinlik(e.target.value)}
              className="w-56"
            >
              {["Anne", "Baba", "Vasi", "Abla", "Ağabey", "Diğer"].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Secim>
            <div>
              <Buton
                yukleniyor={bagla.isPending}
                disabled={!ad.trim() || !soyad.trim() || !eposta.trim()}
                onClick={() => {
                  setBilgi(null);
                  bagla.mutate();
                }}
              >
                Veliyi bağla ve davet gönder
              </Buton>
            </div>
          </div>
        )}

        {liste.data.length === 0 ? (
          <BosDurum
            baslik="Veli bağlanmamış"
            /*
              18 YAŞ ALTI İÇİN BU BİR ENGEL: yasal veli olmadan
              muvafakat kaydedilemez ve öğrencinin hesabı aktifleşmez.
              Boş durum bunu söylüyor.
            */
            aciklama={`${ogrenciAdi} için veli kaydı yok. 18 yaş altı öğrencide yasal veli olmadan muvafakat kaydedilemez ve hesap aktifleşmez.`}
          />
        ) : (
          <ul className="flex flex-col gap-3">
            {liste.data.map((v: VeliOzeti & { verifiedAt?: string | null }) => (
              <li key={v.id} className="rounded-kart border-notr-200 shadow-kart border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-notr-900 text-sm font-medium">
                    {v.firstName} {v.lastName}
                  </span>
                  <Rozet>{v.relation}</Rozet>
                  <Rozet>
                    {v.verifiedAt
                      ? "Bağ doğrulandı"
                      : v.verifiedAt === null
                        ? "Bağ doğrulanmadı"
                        : "Doğrulama durumu alınamadı"}
                  </Rozet>
                  <span className="text-notr-600 text-sm">{v.email}</span>
                  {v.phone && <span className="text-notr-600 text-sm">{v.phone}</span>}
                </div>

                {/* F-MNT-05 — yeniden gönderim mevcut bağlama iznine bağlıdır. */}
                {baglayabilir && v.verifiedAt === null && (
                  <Buton
                    gorunum="ikincil"
                    yukleniyor={dogrulamaGonder.isPending}
                    onClick={() => {
                      setBilgi(null);
                      dogrulamaGonder.mutate(v.id);
                    }}
                  >
                    Doğrulama bağlantısını yeniden gönder
                  </Buton>
                )}

                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                  {ISARETLER.map(([alan, etiket]) => (
                    <label
                      key={alan}
                      className="min-h-dokunma flex cursor-pointer items-center gap-2"
                    >
                      <input
                        type="checkbox"
                        className="accent-marka-600 size-5"
                        checked={v[alan]}
                        disabled={isaret.isPending}
                        onChange={(e) => {
                          setBilgi(null);
                          isaret.mutate({ bagId: v.id, alan, deger: e.target.checked });
                        }}
                      />
                      <span className="text-notr-800 text-sm">{etiket}</span>
                    </label>
                  ))}
                </div>

                {!kaldirabilir ? (
                  /*
                    ================================================================
                    ÇİZİLMEYEN DÜĞMENİN SEBEBİ YAZILIYOR
                    ================================================================
                    Buradan `null` dönülüyordu: yetkisi olmayan uzman
                    hiçbir şey görmüyor ve hiçbir şey okumuyordu. Bir
                    veliyi ayırması gereken uzman düğmeyi arıyor,
                    bulamıyor ve iki sonuçtan birine varıyor — ya sistem
                    bozuk ya kendi gözü kaçırıyor. İkisi de yanlış;
                    doğrusu "bu yetki başkasında".

                    Çizimin kendi cümlesi (`OgrenciDosyasi.dc.html`) ve
                    deponun kendi kuralı: öğrenci `Guvenlik.tsx`
                    "REQUIRED politikada kapatma düğmesi HİÇ çizilmiyor
                    VE SEBEBİ YAZILIYOR — çizip sunucuya reddettirmek,
                    sebebi anlamayan bir kullanıcıyı tekrar tekrar
                    denemeye iterdi" diyor. Burada kuralın yalnızca ilk
                    yarısı uygulanmıştı.

                    Satır BİR KEZ, listenin altında değil her velinin
                    içinde: kaldırmayı düşünen kişi o satıra bakıyor.
                  */
                  <p className="text-notr-600 mt-2 text-xs">
                    Bağlantıyı kaldırma yetkisi yönetimde — bu düğme size çizilmiyor.
                  </p>
                ) : kaldirilan === v.id ? (
                  <div className="mt-3 flex flex-col gap-2">
                    <Alan
                      etiket="Bağlantı neden kaldırılıyor (zorunlu)"
                      value={kaldirGerekce}
                      onChange={(e) => setKaldirGerekce(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <Buton
                        gorunum="tehlike"
                        yukleniyor={kaldir.isPending}
                        disabled={kaldirGerekce.trim().length < 3}
                        onClick={() => kaldir.mutate(v.id)}
                      >
                        Bağlantıyı kaldır
                      </Buton>
                      <Buton gorunum="sessiz" onClick={() => setKaldirilan(null)}>
                        Vazgeç
                      </Buton>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2">
                    <Buton gorunum="sessiz" onClick={() => setKaldirilan(v.id)}>
                      Bağlantıyı kaldır
                      <span className="sr-only">
                        {" "}
                        — {v.firstName} {v.lastName}
                      </span>
                    </Buton>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Kart>
  );
}

// =====================================================================
// DOSYALAR — F-SYS-03
// =====================================================================

function Dosyalar({ ogrenciId }: { ogrenciId: string }) {
  const qc = useQueryClient();
  const dosyaAlani = useRef<HTMLInputElement>(null);
  const [tur, setTur] = useState<DosyaTuru>("PDF");
  const [silinecek, setSilinecek] = useState<string | null>(null);
  const [silGerekce, setSilGerekce] = useState("");
  const [bilgi, setBilgi] = useState<string | null>(null);

  const liste = useQuery({
    queryKey: ["dosyalar", ogrenciId],
    queryFn: () => dosyaListesi(ogrenciId),
    retry: false,
  });
  const kota = useQuery({
    queryKey: ["dosya-kota", ogrenciId],
    queryFn: () => dosyaKotasi(ogrenciId),
    retry: false,
  });

  const tazele = async (): Promise<void> => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["dosyalar", ogrenciId] }),
      qc.invalidateQueries({ queryKey: ["dosya-kota", ogrenciId] }),
    ]);
  };

  const yukle = useMutation({
    mutationFn: (d: File) => dosyaYukle(ogrenciId, d, tur),
    onSuccess: async (d) => {
      setBilgi(`"${d.originalName}" yüklendi.`);
      if (dosyaAlani.current) dosyaAlani.current.value = "";
      await tazele();
    },
  });

  const sil = useMutation({
    mutationFn: (id: string) => dosyaSil(id, silGerekce.trim()),
    onSuccess: async () => {
      setBilgi("Dosya arşivlendi. Depodan kaldırma 30 gün sonraki temizlik işinde.");
      setSilinecek(null);
      setSilGerekce("");
      await tazele();
    },
  });

  /*
    İNDİRME BAĞLANTISI TIKLANDIĞINDA ÜRETİLİYOR, listeyle birlikte
    değil. Bağlantı 15 dakika geçerli; listeyle üretilseydi sayfayı
    açık bırakan kullanıcının bağlantıları çoktan ölmüş olurdu ve
    "indirilemiyor" derdi.
  */
  const indir = useMutation({
    mutationFn: dosyaBaglantisi,
    onSuccess: (s) => {
      window.open(s.url, "_blank", "noopener,noreferrer");
    },
  });

  if (liste.isPending) return <Iskelet satir={2} />;

  if (liste.isError) {
    return (
      <Kart baslik="Dosyalar">
        <BosDurum
          ton="hata"
          baslik="Dosyalar alınamadı"
          aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
        />
      </Kart>
    );
  }

  return (
    <Kart
      baslik="Dosyalar"
      aciklama="Görsel 8 MB, PDF 25 MB. Dosya türü İÇERİKTEN doğrulanır; uzantıyı değiştirmek işe yaramaz."
      eylem={
        kota.data ? (
          <span
            className={
              kota.data.uyariVer ? "text-uyari-600 text-sm font-medium" : "text-notr-600 text-sm"
            }
          >
            {kota.data.okunabilir}
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3">
        {kota.data?.doldu && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            Kota doldu. Yeni dosya yüklenemez; önce eski dosyaları arşivleyin.
          </p>
        )}

        <div className="flex flex-wrap items-end gap-2">
          <Secim
            etiket="Tür"
            value={tur}
            onChange={(e) => setTur(e.target.value as DosyaTuru)}
            className="w-52"
          >
            {Object.entries(DOSYA_TURU).map(([k, a]) => (
              <option key={k} value={k}>
                {a}
              </option>
            ))}
          </Secim>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="dosya-secimi" className="text-notr-800 text-sm font-medium">
              Dosya
            </label>
            <input
              id="dosya-secimi"
              ref={dosyaAlani}
              type="file"
              disabled={kota.data?.doldu || yukle.isPending}
              className="min-h-dokunma file:min-h-dokunma file:rounded-alan file:bg-notr-100 text-sm file:mr-3 file:border-0 file:px-4 file:text-sm file:font-medium"
              onChange={(e) => {
                const d = e.target.files?.[0];
                if (!d) return;
                setBilgi(null);
                yukle.mutate(d);
              }}
            />
          </div>
        </div>

        {yukle.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {yukle.error instanceof ApiHatasi ? yukle.error.message : "Dosya yüklenemedi."}
          </p>
        )}
        {(sil.isError || indir.isError) && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {(sil.error ?? indir.error) instanceof ApiHatasi
              ? ((sil.error ?? indir.error) as ApiHatasi).message
              : "İşlem tamamlanamadı."}
          </p>
        )}
        {bilgi && (
          <p role="status" className="text-notr-700 text-sm">
            {bilgi}
          </p>
        )}

        {liste.data.length === 0 ? (
          <BosDurum
            ikon="bos"
            baslik="Dosya yok"
            aciklama="Bu öğrenciye ait yüklenmiş dosya yok."
          />
        ) : (
          <Tablo
            satirlar={liste.data}
            anahtar={(d) => d.id}
            kartBasligi={0}
            sutunlar={[
              {
                baslik: "Dosya",
                mobil: true,
                className: "font-medium",
                hucre: (d) => d.originalName,
              },
              { baslik: "Tür", mobil: true, hucre: (d) => DOSYA_TURU[d.kind] ?? d.kind },
              { baslik: "Boyut", sayisal: true, hucre: (d) => boyut(d.sizeBytes) },
              { baslik: "Tarih", mobil: true, hucre: (d) => gun(d.createdAt) },
              {
                baslik: "İşlem",
                mobil: true,
                hucre: (d) => (
                  <div className="flex justify-end gap-1">
                    <Buton
                      gorunum="sessiz"
                      yukleniyor={indir.isPending && indir.variables === d.id}
                      onClick={() => indir.mutate(d.id)}
                    >
                      İndir
                      <span className="sr-only"> — {d.originalName}</span>
                    </Buton>
                    <Buton gorunum="sessiz" onClick={() => setSilinecek(d.id)}>
                      Sil
                      <span className="sr-only"> — {d.originalName}</span>
                    </Buton>
                  </div>
                ),
              },
            ]}
            altSatir={(d) =>
              silinecek === d.id ? (
                <div className="border-notr-100 bg-notr-50 flex flex-col gap-2 border-b px-3 py-3">
                  <Alan
                    etiket="Silme gerekçesi (zorunlu)"
                    value={silGerekce}
                    onChange={(e) => setSilGerekce(e.target.value)}
                    yardim="Dosya arşivlenir, hemen yok olmaz — gerekçe kayda geçer."
                  />
                  <div className="flex gap-2">
                    <Buton
                      gorunum="tehlike"
                      yukleniyor={sil.isPending}
                      disabled={silGerekce.trim().length < 3}
                      onClick={() => sil.mutate(d.id)}
                    >
                      Arşivle
                    </Buton>
                    <Buton gorunum="sessiz" onClick={() => setSilinecek(null)}>
                      Vazgeç
                    </Buton>
                  </div>
                </div>
              ) : null
            }
          />
        )}
      </div>
    </Kart>
  );
}

// =====================================================================
// PROFİL DÜZENLEME — F-PPL
// =====================================================================

const SINIF_ADI: Record<Sinif, string> = {
  G5: "5. sınıf",
  G6: "6. sınıf",
  G7: "7. sınıf",
  G8: "8. sınıf",
  G9: "9. sınıf",
  G10: "10. sınıf",
  G11: "11. sınıf",
  G12: "12. sınıf",
  GRADUATE: "Mezun",
  OTHER: "Diğer",
};

/**
 * `PATCH /ogrenciler/:id` yazılıydı, hiçbir ekran çağırmıyordu:
 * yanlış yazılmış bir ad, değişen bir telefon ya da sınıf atlayan
 * bir öğrenci panelden düzeltilemiyordu.
 *
 * ================================================================
 * DOĞUM TARİHİ BU FORMDA YOK — SUNUCU DA KABUL ETMİYOR
 * ================================================================
 * Şema `birthDate` almıyor ve sebebi tek cümleyle yazılı: tarih
 * ileri çekilerek 18 yaş altı bir öğrencinin veli muvafakati kapısı
 * atlatılabilirdi (F-PPL-05b). Alanı ekranda göstermemek korumanın
 * yerine geçmiyor; koruma sunucuda.
 *
 * ================================================================
 * AD LİSTEDEN OKUNUYOR, AYRI UÇTAN DEĞİL
 * ================================================================
 * `GET /ogrenciler/:id` ucu var ama listeden gelen satır bu formun
 * ihtiyacı olan her alanı zaten taşıyor. İkinci bir istek atmak,
 * aynı veriyi iki kaynaktan okumak ve ikisi ayrıştığında hangisinin
 * doğru olduğunu tartışmak demekti.
 */
function Profil({ ogrenciId, duzenleyebilir }: { ogrenciId: string; duzenleyebilir: boolean }) {
  const qc = useQueryClient();
  const [acik, setAcik] = useState(false);

  const liste = useQuery({
    queryKey: ["ogrenciler"],
    queryFn: ogrenciListesi,
    retry: false,
  });

  const kisi: OgrenciSatiri | undefined = liste.data?.ogrenciler.find((o) => o.id === ogrenciId);

  if (liste.isPending) return <Iskelet satir={3} />;

  if (!kisi) {
    // Kapsam disi ya da arsivlenmis. "Bulunamadi" demek yanlis olurdu:
    // kayit var olabilir ve yalnizca bu kullaniciya kapali olabilir.
    return null;
  }

  return (
    <Kart baslik={`${kisi.ad} ${kisi.soyad}`} aciklama={`Öğrenci no ${kisi.studentNo}`}>
      {acik ? (
        <ProfilFormu
          kisi={kisi}
          onKapat={() => setAcik(false)}
          onKaydedildi={async () => {
            setAcik(false);
            await qc.invalidateQueries({ queryKey: ["ogrenciler"] });
          }}
        />
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <div className="flex gap-2">
              <dt className="text-notr-600">Sınıf</dt>
              <dd className="text-notr-900">{SINIF_ADI[kisi.sinif as Sinif] ?? kisi.sinif}</dd>
            </div>
            {kisi.muvafakatBekliyor && (
              <div className="flex gap-2">
                <dt className="text-notr-600">Durum</dt>
                <dd className="text-uyari-600">Veli muvafakati bekliyor</dd>
              </div>
            )}
          </dl>
          {kisi.davetBekliyor && (
            <DavetYenidenGonder
              hedef={{ ogrenciId }}
              izin={kisi.davetIzni}
              tumKayitlarGerekli={kisi.davetIzni === "mentorum.erisim.yonet"}
            />
          )}
          {duzenleyebilir && (
            <Buton gorunum="ikincil" onClick={() => setAcik(true)}>
              Bilgileri düzenle
            </Buton>
          )}
        </div>
      )}

      {/*
        T.C. AYRI EYLEM — profil formunun İÇİNDE DEĞİL.

        Sunucuda ayrı uç ve ayrı izin var (`kisi.tc_kimlik.degistir`) ve
        o izin uzmanda YOK. Alanı profil formuna koysaydık, izni olmayan
        bir uzman formu doldurup kaydeder ve KAYDIN TAMAMI reddedilirdi
        — ad değişikliği de giderdi.

        `duzenleyebilir` yalnızca gereksiz bir denemeyi engelliyor;
        koruma sunucuda ve orada kalıyor.
      */}
      {duzenleyebilir && (
        <div className="border-notr-200 mt-3 border-t pt-3">
          <TcKimlikDuzenle
            mevcutMaskeli={kisi.nationalIdMaskeli}
            kaydet={(yeni) => ogrenciTcKimlikDegistir(kisi.id, yeni)}
            onKaydedildi={() => void qc.invalidateQueries({ queryKey: ["ogrenciler"] })}
          />
        </div>
      )}
    </Kart>
  );
}

function ProfilFormu({
  kisi,
  onKapat,
  onKaydedildi,
}: {
  kisi: OgrenciSatiri;
  onKapat: () => void;
  onKaydedildi: () => void;
}) {
  const [ad, setAd] = useState(kisi.ad);
  const [soyad, setSoyad] = useState(kisi.soyad);
  const [sinif, setSinif] = useState(kisi.sinif);

  const mut = useMutation({
    mutationFn: () => {
      // YALNIZCA DEĞİŞEN alanlar. Hepsini göndermek denetim kaydına
      // değişmemiş alanları da yazdırırdı.
      const veri: { firstName?: string; lastName?: string; gradeLevel?: string } = {};
      if (ad.trim() !== kisi.ad) veri.firstName = ad.trim();
      if (soyad.trim() !== kisi.soyad) veri.lastName = soyad.trim();
      if (sinif !== kisi.sinif) veri.gradeLevel = sinif;
      return ogrenciProfilGuncelle(kisi.id, veri);
    },
    onSuccess: onKaydedildi,
  });

  const degisti = ad.trim() !== kisi.ad || soyad.trim() !== kisi.soyad || sinif !== kisi.sinif;
  const eksik = ad.trim().length === 0 || soyad.trim().length === 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <Alan etiket="Ad" value={ad} onChange={(e) => setAd(e.target.value)} />
        <Alan etiket="Soyad" value={soyad} onChange={(e) => setSoyad(e.target.value)} />
        <Secim etiket="Sınıf" value={sinif} onChange={(e) => setSinif(e.target.value)}>
          {SINIFLAR.map((s) => (
            <option key={s} value={s}>
              {SINIF_ADI[s]}
            </option>
          ))}
        </Secim>
      </div>

      <p className="text-notr-600 text-xs">
        Doğum tarihi buradan değişmez: ileri çekilerek 18 yaş altı bir öğrencinin veli muvafakati
        kapısı atlatılabilirdi{/* F-PPL-05b */}.
      </p>

      {mut.isError && (
        <p role="alert" className="text-hata-700 text-sm font-medium">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Profil güncellenemedi."}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Buton yukleniyor={mut.isPending} disabled={!degisti || eksik} onClick={() => mut.mutate()}>
          Kaydet
        </Buton>
        <Buton gorunum="ikincil" onClick={onKapat}>
          Vazgeç
        </Buton>
      </div>
    </div>
  );
}

// =====================================================================
// ONBOARDING KONTROL LİSTESİ — F-PPL-09
// =====================================================================

/**
 * ================================================================
 * PENCERE KAPANDIYSA DA GÖSTERİLİYOR
 * ================================================================
 * İlk 14 gün geçince liste kaybolmuyor, "pencere kapandı" diye
 * işaretleniyor. Kaybolsaydı, onboarding'i yarım kalmış bir öğrenci
 * on beşinci gün sorunsuz görünürdü — oysa eksikler duruyor.
 *
 * ================================================================
 * TAMAMI BİTMİŞSE KÜÇÜLÜYOR
 * ================================================================
 * Altı maddesi de biten bir öğrencide altı satır göstermek, dosya
 * ekranının yarısını bitmiş işlerle doldururdu. Tek satır özet
 * yeterli; ayrıntı açılabiliyor.
 */
function Onboarding({ ogrenciId }: { ogrenciId: string }) {
  const [acik, setAcik] = useState(false);

  const { data, isPending, isError } = useQuery({
    queryKey: ["onboarding", ogrenciId],
    queryFn: () => onboardingDurumu(ogrenciId),
    retry: false,
  });

  if (isPending) return <Iskelet satir={2} />;
  // Yetkisi olmayana hiçbir şey gösterilmiyor.
  if (isError) return null;

  const hepsiBitti = data.tamamlanan === data.toplam;
  const yuzde = data.oran === null ? null : Math.round(data.oran * 100);

  return (
    <Kart
      baslik="Onboarding"
      /*
        SAYI SÖZLEŞMEDEN. Burada "14" iki cümlede ELLE yazılıydı ve
        sunucudaki sabitin kopyasıydı; sabit değişse bu iki cümle eski
        sayıyı söylemeye devam ederdi — sessizce, çünkü hiçbir şey
        patlamaz. Bu depo bu hatayı yaşadı: ekran "Bağlantı 7 gün
        geçerlidir" derken sunucu 3 gün uyguluyordu.
      */
      aciklama={
        data.penceKapandi
          ? `Kayıttan sonraki ${ONBOARDING_PENCERE_METNI} doldu.`
          : `Kayıttan sonraki ${ONBOARDING_PENCERE_METNI}.` /* F-PPL-09 */
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-notr-800 text-sm">
            {/*
              `null` = madde yok. "%0" yazmak, hiçbirini yapmamış bir
              öğrenciyle hiç maddesi olmayanı aynı gösterirdi.
            */}
            {yuzde === null ? (
              <span className="text-notr-600">Madde yok</span>
            ) : (
              <>
                <strong>
                  {data.tamamlanan}/{data.toplam}
                </strong>{" "}
                tamamlandı (%{yuzde})
              </>
            )}
          </span>

          {data.geciken > 0 && <Rozet ton="hata">{data.geciken} madde gecikti</Rozet>}
          {hepsiBitti && data.toplam > 0 && <Rozet ton="onay">Tamam</Rozet>}

          <Buton gorunum="sessiz" onClick={() => setAcik(!acik)}>
            {acik ? "Kapat" : "Ayrıntı"}
          </Buton>
        </div>

        {acik && (
          <ul className="flex flex-col gap-1">
            {data.maddeler.map((m) => (
              <li
                key={m.kod}
                className="rounded-alan bg-notr-50 flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
              >
                <span
                  aria-hidden="true"
                  className={m.tamamlandiMi ? "text-onay-700" : "text-notr-600"}
                >
                  {m.tamamlandiMi ? "✓" : "○"}
                </span>
                <span className={m.tamamlandiMi ? "text-notr-600 line-through" : "text-notr-800"}>
                  {m.baslik}
                </span>
                {/*
                  GECİKME yalnızca tamamlanmamış maddede. Geç de olsa
                  yapılmış bir iş, bekleyen bir iş değildir.
                */}
                {m.gecikti && (
                  <span className="text-hata-700 text-xs">
                    · {new Date(m.sonTarih).toLocaleDateString("tr-TR")} tarihinde bitmeliydi
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        <p className="text-notr-600 text-xs">
          Maddeler elle işaretlenmiyor: her biri sistemdeki gerçek kayıttan okunuyor (görüşme,
          kaynak listesi, değerlendirme, yayınlanmış program, öğrencinin ilk veri girişi, veli
          toplantısı).
        </p>
      </div>
    </Kart>
  );
}
